// src/components/AuthorList.js
import React, { useState, useEffect } from 'react';
import { getAuthors, deleteAuthor } from '../services/authorService';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Container,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function AuthorList() {
  const [authors, setAuthors] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedAuthors,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(authors, 10);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      const data = await getAuthors();
      setAuthors(data || []);
    } catch (error) {
      console.error('Error fetching authors:', error);
      setError('Failed to load authors.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this author?')) {
      try {
        await deleteAuthor(id);
        fetchAuthors();
      } catch (error) {
        console.error('Error deleting author:', error);
        setError('Failed to delete author.');
      }
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <Box mt={4}>
          <AdminPageHeader
            eyebrow="Content"
            title="Authors"
            description="Manage author profiles and the social links attached to published content."
            action={<Button variant="contained" onClick={() => navigate('/authors/create')}>Create Author</Button>}
          />
          {error ? <Box sx={{ color: 'error.main', mt: 2 }}>{error}</Box> : null}
          <AdminSection sx={{ p: 0, overflow: 'hidden', mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Social Links</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAuthors.map((author) => (
                  <TableRow key={author.authorId}>
                    <TableCell>{author.name}</TableCell>
                    <TableCell>{author.email}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {author.facebookLink && <a href={author.facebookLink}>Facebook</a>}
                        {author.twitterLink && <a href={author.twitterLink}>Twitter</a>}
                        {author.linkedinLink && <a href={author.linkedinLink}>LinkedIn</a>}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/authors/edit/${author.authorId}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(author.authorId)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {authors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <AdminEmptyState title="No authors found" description="Create an author to attach editorial ownership to blog posts." />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default AuthorList;
